import { initMerchantApiClient, isExpiredToken } from '@src/data/api/api-client'
import { getUser } from '@src/services/Authentification'
import { reportErrorToConsole } from '@src/services/error-logger'
import { EPermissions } from '@src/types/Constants'
import { AxiosError } from 'axios'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { UserDtoFromJwt } from '../types/User'

export interface TAppStore {
  user: UserDtoFromJwt | null
  reloadUser: () => void
}
export const AppContext = createContext<TAppStore | null>(null)

let defaultUser: UserDtoFromJwt | null = getUser()
if (defaultUser?.exp && isExpiredToken(defaultUser.exp)) defaultUser = null

export function useAppStore() {
  const [user, setUser] = useState<UserDtoFromJwt | null>(defaultUser)

  const handleApiError = useCallback(
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        setUser(null)
      }
      reportErrorToConsole(error)
    },
    [setUser],
  )

  initMerchantApiClient(handleApiError)

  const reloadUser = useCallback(() => {
    setUser(getUser())
    initMerchantApiClient(handleApiError)
  }, [setUser, handleApiError])

  // #66555 — the Iceberg JWT in localStorage is shared between tabs, and Iceberg
  // employees can switch merchant from another tab. Requests from this tab already
  // authenticate as the new merchant (the request interceptor reads the token at call
  // time); re-sync the in-memory user when the tab regains focus so the queries that
  // send user.merchantId explicitly (header banner, users page) follow — consistent
  // with the dashboard list, which refetches on window focus. A cleared token
  // (mid-switch state) and same-merchant refreshes are ignored.
  useEffect(() => {
    const syncUserWithStoredToken = () => {
      if (document.visibilityState !== 'visible') return
      const next = getUser()
      if (next && user && next.merchantId !== user.merchantId) {
        reloadUser()
      }
    }
    window.addEventListener('focus', syncUserWithStoredToken)
    document.addEventListener('visibilitychange', syncUserWithStoredToken)
    return () => {
      window.removeEventListener('focus', syncUserWithStoredToken)
      document.removeEventListener('visibilitychange', syncUserWithStoredToken)
    }
  }, [user, reloadUser])

  return useMemo(
    () => ({
      user,
      reloadUser,
    }),
    [user, reloadUser],
  )
}

export function useUser() {
  const ctx = useContext(AppContext)
  return ctx?.user
}
export function useUserPermissions() {
  const ctx = useContext(AppContext)
  return useMemo(() => {
    const roles = ctx?.user?.roles ? ctx.user.roles.split(',').map((r) => r.trim()) : []
    const rolesSet = new Set(roles)
    return {
      hasDetailAccess: rolesSet.has(EPermissions.HasDetailAccess),
      canCreateApplications: rolesSet.has(EPermissions.CanCreateApplications),
      isAdmin: rolesSet.has(EPermissions.IsAdmin),
      canSendWelcomeMessage: rolesSet.has(EPermissions.CanSendWelcomeMessage),
    }
  }, [ctx?.user?.roles])
}
